import React, { useState } from "react";
import { connect } from "react-redux";
import * as selectors from "../../selectors/selectors";
import { IAccountStateProps, IAccountProps } from "../Account/types";
import { IUserAccount } from "../types/common";
import { IState } from "../reducer";
import { SideMenuArrowContext, SideMenuCloseArrow } from "../../components/utils/SideMenu/SideMenuCloseArrow";
import TopBar from "../../components/utils/TopBar";
import { SideMenu } from "../../components/utils/SideMenu/SideMenu.container";
import SubTopbar from "../../components/utils/SubTopbar";
import { UserContextComponent } from "../../context/users/UserContext";
import AccountEditor from "./AccountEditor.component";
import { api } from "../../dependencies";
import { IEditAccountOperations, ElementState } from "./types";
import { PreferredLocale } from "../../utils/locales";
import { ClientFeature } from "../../parsers/login";

export const AccountManagementComponent: React.FC<IAccountProps> = (props): JSX.Element => {
  const { organizationName, userName } = props;

  const [menuExpanded, setMenuExpanded] = useState(true);
  const { isOpen } = React.useContext(SideMenuArrowContext);
  const toogleMenu = () => setMenuExpanded(!menuExpanded);

  return (
    <>
      <TopBar showToggleMenu={true} onToggleMenuClick={toogleMenu} userName={userName} />
      <SideMenuCloseArrow />
      <SideMenu
        expanded={menuExpanded}
        organizationName={organizationName}
        userName={userName}
        onClose={toogleMenu}
        isOpen={isOpen}
      />
      <SubTopbar withSidebar={true} isOpen={isOpen}>
        <UserContextComponent
          isOrgAdmin={props.isOrgAdmin}
          accountId={props.accountId}
          organizationId={props.organizationId}
        >
          <AccountEditorWrapper
            activeUserId={props.accountId}
            organizationId={props.organizationId}
            accountId={props.match.params.accountId}
            isOrgAdmin={props.isOrgAdmin}
          />
        </UserContextComponent>
      </SubTopbar>
    </>
  );
};

interface AccountEditorWrapperProps {
  accountId: string;
  activeUserId: string;
  isOrgAdmin: boolean;
  organizationId: string;
}

const AccountEditorWrapper = (props: AccountEditorWrapperProps) => {
  const { accountId, activeUserId, organizationId, isOrgAdmin } = props;

  const [selectedAccount, setSelectedAccount] = useState<IUserAccount | undefined>(undefined);
  const [emailAddress, setEmailAddress] = React.useState<ElementState>({ value: undefined, isDeleted: false });
  const [accountAlias, setAccountAlias] = React.useState<ElementState>({ value: undefined, isDeleted: false });
  const [selectedLocale, setSelectedLocale] = React.useState<PreferredLocale | null>(null);
  const [clientFeatures, setClientFeatures] = React.useState<ClientFeature[] | undefined>(undefined);

  const modifyTitle = (title: string) => api.users.modifyTitle(activeUserId, `acc.${accountId}?`, title);

  const addEmailAddress = (address: string) => api.users.addEmailAddress(activeUserId, `acc.${accountId}?`, address);

  const modifyEmailAddress = (address: string) =>
    api.users.modifyEmailAddress(activeUserId, `acc.${accountId}?`, address);

  const deleteEmailAddress = () => api.users.deleteEmailAddress(activeUserId, `acc.${accountId}?`);

  const undeleteEmailAddress = () => api.users.undeleteEmailAddress(activeUserId, `acc.${accountId}?`);

  const addAccountAlias = (orgId: string, alias: string) =>
    api.users.addAccountAlias(orgId, activeUserId, `acc.${accountId}?`, alias);

  const modifyAccountAlias = (orgId: string, alias: string) =>
    api.users.modifyAccountAlias(orgId, activeUserId, `acc.${accountId}?`, alias);

  const deleteAccountAlias = () => api.users.deleteAccountAlias(activeUserId, `acc.${accountId}?`);

  const undeleteAccountAlias = () => api.users.undeleteAccountAlias(activeUserId, `acc.${accountId}?`);

  const modifyPassword = (newPassword: string) => api.users.modifyPassword(activeUserId, accountId, newPassword);

  const modifyPreferredLocale = (newLocale: PreferredLocale | null) =>
    api.users.modifyPreferredLocale(activeUserId, `acc.${accountId}?`, newLocale);

  const insertClientFeature = (newClientFeature: ClientFeature) =>
    api.users.insertClientFeature(activeUserId, `acc.${accountId}?`, newClientFeature);

  const deleteClientFeature = (clientFeature: ClientFeature) =>
    api.users.deleteClientFeature(activeUserId, `acc.${accountId}?`, clientFeature);

  const modifyOwningOrg = (newOwningOrgId: string) =>
    api.users.modifyOwningOrg(activeUserId, `acc.${accountId}?`, newOwningOrgId);

  const migrateAccountAlias = (oldOwningOrgId: string) =>
    api.users.migrateAliasToNewOrg(activeUserId, `acc.${accountId}?`, oldOwningOrgId);

  const deleteAccount = () => api.users.deleteUser(activeUserId, `acc.${accountId}?`);

  const editAccountOperations = {
    addAccountAlias,
    addEmailAddress,
    deleteAccount,
    deleteAccountAlias,
    deleteClientFeature,
    deleteEmailAddress,
    insertClientFeature,
    migrateAccountAlias,
    modifyAccountAlias,
    modifyEmailAddress,
    modifyOwningOrg,
    modifyPassword,
    modifyPreferredLocale,
    modifyTitle,
    undeleteAccountAlias,
    undeleteEmailAddress,
  } as IEditAccountOperations;

  React.useEffect(() => {
    api.users.getUserById(activeUserId, `acc.${accountId}?`).then(acc => {
      const accountOwnerOrg = acc.owner;
      const accAlias = acc.aliases.elems.find(a => a.state.namespace === `org.${accountOwnerOrg}`);
      setSelectedAccount(acc);
      setEmailAddress({
        isDeleted: acc.emailAddresses.elems[0]?.deleted ?? false,
        value: acc.emailAddresses.elems[0]?.state.address,
      });
      setAccountAlias({
        isDeleted: accAlias?.deleted ?? false,
        value: accAlias?.state.alias.name,
      });
      setSelectedLocale(acc.preferredLocale);
      setClientFeatures(acc.oldClientFeatures);
    });
  }, []);

  return (
    <AccountEditor
      selectedAccount={selectedAccount}
      editAccountOperations={editAccountOperations}
      emailAddress={emailAddress}
      setEmailAddress={setEmailAddress}
      activeUserId={activeUserId}
      accountId={accountId}
      accountAlias={accountAlias}
      setAccountAlias={setAccountAlias}
      selectedLocale={selectedLocale}
      setSelectedLocale={setSelectedLocale}
      clientFeatures={clientFeatures}
      setClientFeatures={setClientFeatures}
      activeOrgId={organizationId}
      isOrgAdmin={isOrgAdmin}
    />
  );
};

const mapStateToProps = (state: IState): IAccountStateProps => ({
  accountId: selectors.accountId(state),
  isOrgAdmin: selectors.isOrgAdmin(state),
  organizationId: selectors.organizationId(state),
  organizationName: selectors.organizationName(state),
  userName: selectors.accountName(state),
});

export const AccountManagement = connect(mapStateToProps)(AccountManagementComponent);
